import { useState, useEffect, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Loader2, Plus, Save, X, Clock, Phone, Sun, Moon, Sunset, Search, UserCheck, UserX, Edit, Trash2, Check } from 'lucide-react';
import { teamAPI, userAPI } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const SHIFT_TYPES = [
  { name: 'Morning', color: 'bg-amber-100 text-amber-800 border-amber-300', time: '06:00-14:00' },
  { name: 'Day', color: 'bg-blue-100 text-blue-800 border-blue-300', time: '09:00-18:00' },
  { name: 'Evening', color: 'bg-purple-100 text-purple-800 border-purple-300', time: '14:00-22:00' },
  { name: 'Night', color: 'bg-slate-700 text-white border-slate-500', time: '22:00-06:00' },
  { name: 'On-Call', color: 'bg-rose-100 text-rose-700 border-rose-300', time: 'On demand' },
  { name: 'Off', color: 'bg-slate-50 text-slate-400 border-slate-200 border-dashed', time: '' },
];

const VIEWS = ['month', 'week', 'day'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getMonthDays(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const days = [];
  const startDay = first.getDay();
  for (let i = startDay - 1; i >= 0; i--) days.push({ date: new Date(year, month, -i), isCurrentMonth: false });
  for (let i = 1; i <= last.getDate(); i++) days.push({ date: new Date(year, month, i), isCurrentMonth: true });
  while (days.length % 7 !== 0) days.push({ date: new Date(year, month + 1, days.length - last.getDate() - startDay + 1), isCurrentMonth: false });
  return days;
}
function getWeekDays(baseDate) {
  const start = new Date(baseDate); start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
}
// Use local date parts — toISOString() converts to UTC which shifts the date in IST
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function isToday(d) { const t = new Date(); return fmtDate(d) === fmtDate(t); }

export function ShiftCalendarPage() {
  const { user } = useAuth();
  const roleNorm = (user?.role_name || '').toLowerCase().replace(/\s+/g, '_');
  const isManager = ['admin', 'it_manager'].includes(roleNorm);
  // IT Support and Approver can view but not edit
  const isReadOnly = ['it_support', 'approver'].includes(roleNorm);
  const [view, setView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(null);
  const [showManageShifts, setShowManageShifts] = useState(false);
  const [newShiftName, setNewShiftName] = useState('');
  const [newShiftStart, setNewShiftStart] = useState('09:00');
  const [newShiftEnd, setNewShiftEnd] = useState('18:00');
  const [editingShift, setEditingShift] = useState(null); // {id, name, start_time, end_time}
  const [editForm, setEditForm] = useState({ name: '', start_time: '', end_time: '' });
  const [search, setSearch] = useState('');
  const [appFilter, setAppFilter] = useState('');
  const [teams, setTeams] = useState([]);
  // isManager already set above, remove duplicate

  const fetch = async () => {
    setLoading(true);
    try {
      // Fetch assignments for a generous date range (±5 weeks from current date)
      // so the week/month/day views always have complete data without fetching everything
      const today = new Date();
      const from = new Date(today); from.setDate(from.getDate() - 35);
      const to   = new Date(today); to.setDate(to.getDate() + 35);
      const dateFrom = fmtDate(from);
      const dateTo   = fmtDate(to);

      const [sRes, aRes, uRes, tRes] = await Promise.allSettled([
        teamAPI.listShifts(),
        teamAPI.listShiftAssignments({ date_from: dateFrom, date_to: dateTo }),
        userAPI.list({ page_size: 1000 }),
        teamAPI.list(),
      ]);
      if (sRes.status === 'fulfilled') setShifts(sRes.value.data?.results || sRes.value.data || []);
      if (aRes.status === 'fulfilled') setAssignments(aRes.value.data?.results || aRes.value.data || []);
      if (uRes.status === 'fulfilled') setUsers(uRes.value.data?.results || uRes.value.data || []);
      if (tRes.status === 'fulfilled') setTeams(tRes.value.data?.results || tRes.value.data || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []); // eslint-disable-line

  const assignmentMap = useMemo(() => {
    const m = {}; assignments.forEach(a => { m[`${a.user}_${a.date}`] = a; }); return m;
  }, [assignments]);

  const getAssignment = (userId, date) => assignmentMap[`${userId}_${fmtDate(date)}`];

  // All dates in the current week (for dynamic assigned-count in Shift Types table)
  const currentWeekDates = useMemo(() => getWeekDays(currentDate).map(d => fmtDate(d)), [currentDate]);

  // Derive unique applications from team data — needs `application` field in TeamListSerializer
  const applications = useMemo(() => {
    const seen = new Set();
    const apps = [];
    teams.forEach(t => {
      const appId = t.application ? String(t.application) : null;
      if (appId && t.application_name && !seen.has(appId)) {
        seen.add(appId);
        apps.push({ id: appId, name: t.application_name });
      }
    });
    return apps.sort((a, b) => a.name.localeCompare(b.name));
  }, [teams]);

  // Team IDs belonging to the selected application
  const appTeamIds = useMemo(() => {
    if (!appFilter) return null;
    return new Set(
      teams.filter(t => String(t.application) === appFilter).map(t => String(t.id))
    );
  }, [appFilter, teams]);

  // Member IDs (user UUIDs) for the selected application — using user.team_memberships
  const appMemberIds = useMemo(() => {
    if (!appFilter || !appTeamIds) return null;
    const ids = new Set();
    users.forEach(u => {
      (u.team_memberships || []).forEach(tm => {
        if (appTeamIds.has(String(tm.team_id))) ids.add(u.id);
      });
    });
    return ids;
  }, [appFilter, appTeamIds, users]);

  // Filter users: by application membership + optional name search
  const filteredUsers = useMemo(() => {
    let list = (isManager || isReadOnly) ? users : users.filter(u => u.id === user?.id);
    if (appFilter && appMemberIds) {
      list = list.filter(u => appMemberIds.has(u.id));
    }
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(u =>
        (u.full_name || '').toLowerCase().includes(s) ||
        (u.email || '').toLowerCase().includes(s) ||
        (u.role_name || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [users, search, appFilter, appMemberIds, isManager, isReadOnly, user]);

  // Dynamic users-assigned count: unique users with this shift IN the current week view
  // Respects the application filter so count matches what's visible in the calendar.
  const getShiftUserCount = (shiftId) => {
    const weekAssignments = assignments.filter(a =>
      a.shift === shiftId && currentWeekDates.includes(a.date)
    );
    const uniqueUserIds = new Set(weekAssignments.map(a => a.user));
    // If an app filter is active, only count users visible in the filtered calendar
    if (appFilter && appMemberIds) {
      return [...uniqueUserIds].filter(uid => appMemberIds.has(uid)).length;
    }
    return uniqueUserIds.size;
  };

  const handleAssign = async (userId, date, shiftName) => {
    if (shiftName === 'Off') {
      try { await teamAPI.deleteShiftByUserDate(userId, fmtDate(date)); toast.success('Marked as Off'); setShowAssign(null); fetch(); }
      catch { toast.error('Failed'); }
      return;
    }
    let shiftId;
    if (shiftName === 'On-Call') {
      const dayShift = shifts.find(s => s.name === 'Day') || shifts[0];
      if (!dayShift) { toast.error('No shift types. Create them first.'); return; }
      shiftId = dayShift.id;
    } else {
      const shift = shifts.find(s => s.name === shiftName);
      if (!shift) { toast.error(`Shift "${shiftName}" not found.`); return; }
      shiftId = shift.id;
    }
    try {
      await teamAPI.createShiftAssignment({ user: userId, shift: shiftId, date: fmtDate(date), is_on_call: shiftName === 'On-Call' });
      toast.success(`${shiftName} set`); setShowAssign(null); fetch();
    } catch (err) { toast.error(typeof err.response?.data === 'string' ? err.response.data : JSON.stringify(err.response?.data) || 'Failed'); }
  };

  const [savingShift, setSavingShift] = useState(false);

  const handleCreateShift = async () => {
    if (!newShiftName.trim()) { toast.error('Name required'); return; }
    if (savingShift) return;
    setSavingShift(true);
    try {
      await teamAPI.createShift({ name: newShiftName.trim(), start_time: newShiftStart, end_time: newShiftEnd });
      toast.success('Shift created');
      setNewShiftName('');
      fetch();
    } catch (err) {
      const detail = err.response?.data?.name?.[0] || err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed';
      toast.error(detail.includes('already exists') ? `A shift named "${newShiftName.trim()}" already exists.` : detail);
    } finally { setSavingShift(false); }
  };

  const handleStartEdit = (shift) => {
    setEditingShift(shift.id);
    setEditForm({ name: shift.name, start_time: shift.start_time, end_time: shift.end_time });
  };

  const handleSaveEdit = async (shiftId) => {
    if (!editForm.name) { toast.error('Name required'); return; }
    try {
      await teamAPI.updateShift(shiftId, editForm);
      toast.success('Shift updated');
      setEditingShift(null);
      fetch();
    } catch (err) { toast.error(JSON.stringify(err.response?.data) || 'Failed'); }
  };

  const handleDeleteShift = async (shift) => {
    // Count how many assignments use this shift
    const assignedCount = assignments.filter(a => a.shift === shift.id).length;
    if (assignedCount > 0) {
      toast.error(`Cannot delete "${shift.name}" — ${assignedCount} user assignment(s) exist. Remove them from the calendar first.`);
      return;
    }
    if (!window.confirm(`Delete shift "${shift.name}"? This cannot be undone.`)) return;
    try {
      await teamAPI.deleteShift(shift.id);
      toast.success(`Shift "${shift.name}" deleted`);
      fetch();
    } catch (err) {
      const detail = err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed';
      toast.error(detail);
    }
  };

  const toggleAvailability = async (u) => {
    if (!isManager && u.id !== user?.id) { toast.error('You can only update your own availability'); return; }
    try {
      await userAPI.update(u.id, { is_available: !u.is_available });
      toast.success(`${u.full_name}: ${u.is_available ? 'Marked Unavailable' : 'Marked Available'}`);
      fetch();
    } catch { toast.error('Failed'); }
  };

  const handleQuickSet = (userId, date) => {
    if (!isManager && userId !== user?.id) { toast.error('You can only update your own shift'); return; }
    setShowAssign({ userId, date });
  };

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else if (view === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir);
    setCurrentDate(d);
  };

  const monthDays = useMemo(() => getMonthDays(currentDate.getFullYear(), currentDate.getMonth()), [currentDate]);
  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);
  const title = view === 'month' ? `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`
    : view === 'week' ? `Week of ${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;

  const ShiftBadge = ({ assignment }) => {
    if (!assignment) return null;
    const st = SHIFT_TYPES.find(s => s.name.toLowerCase() === (assignment.shift_name || '').toLowerCase()) || (assignment.is_on_call ? SHIFT_TYPES[4] : null);
    if (!st) return null;
    return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${st.color}`}>{assignment.is_on_call ? 'On-Call' : (assignment.shift_name || st.name)}</span>;
  };

  const displayUsers = appFilter ? filteredUsers : users;
  const availableCount = displayUsers.filter(u => u.is_available).length;
  const unavailableCount = displayUsers.filter(u => !u.is_available).length;

  return (
    <div data-testid="shift-calendar-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2"><Calendar className="w-6 h-6 text-teal-600" /> Shift Calendar</h2>
          <p className="text-sm text-slate-500 mt-1">Plan shifts and manage team availability. Unavailable users are skipped in auto-assignment.</p>
          {isReadOnly && <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 px-2.5 py-1 rounded-full font-medium mt-1 inline-block">Read-only view</span>}
        </div>
        {isManager && (
          <button onClick={() => setShowManageShifts(!showManageShifts)} className="bg-white dark:bg-slate-800 border dark:border-slate-700 px-4 py-2 rounded-xl text-sm hover:bg-slate-50 flex items-center gap-1.5"><Plus className="w-4 h-4" /> Manage Shifts</button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl p-3 border-l-4 border-l-emerald-500">
          <div className="text-lg font-bold text-emerald-600">{availableCount}</div>
          <div className="text-xs text-slate-500">Available for Assignment</div>
        </div>
        <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl p-3 border-l-4 border-l-rose-500">
          <div className="text-lg font-bold text-rose-600">{unavailableCount}</div>
          <div className="text-xs text-slate-500">Unavailable / On Leave</div>
        </div>
        <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-xl p-3 border-l-4 border-l-sky-500">
          <div className="text-lg font-bold text-sky-600">{displayUsers.length}</div>
          <div className="text-xs text-slate-500">Total Users{appFilter ? ' (filtered)' : ''}</div>
        </div>
      </div>

      {/* Manage Shifts Panel */}
      {showManageShifts && isManager && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm p-5 mb-4">
          <h3 className="font-semibold dark:text-white mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4 text-sky-500" /> Shift Types
          </h3>

          {/* Shifts Table */}
          <div className="overflow-x-auto mb-5">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900 border-b dark:border-slate-700">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Shift Name</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Start Time</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">End Time</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Duration</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Users Assigned</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {shifts.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-slate-400 py-6 text-sm">No shifts yet. Create one below.</td></tr>
                )}
                {shifts.map(s => {
                  const st = SHIFT_TYPES.find(t => t.name === s.name) || { color: 'bg-sky-100 text-sky-800 border-sky-300' };
                  const assignedCount = getShiftUserCount(s.id);
                  const isEditing = editingShift === s.id;

                  // Calculate duration
                  const calcDuration = (start, end) => {
                    const [sh, sm] = start.split(':').map(Number);
                    const [eh, em] = end.split(':').map(Number);
                    let mins = (eh * 60 + em) - (sh * 60 + sm);
                    if (mins < 0) mins += 1440; // overnight
                    const h = Math.floor(mins / 60);
                    const m = mins % 60;
                    return `${h}h${m > 0 ? ` ${m}m` : ''}`;
                  };

                  return (
                    <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                      {isEditing ? (
                        <>
                          <td className="px-4 py-2">
                            <input value={editForm.name} onChange={e => setEditForm(p => ({...p, name: e.target.value}))}
                              className="border dark:border-slate-600 rounded-lg px-2.5 py-1.5 text-sm w-32 dark:bg-slate-900 dark:text-white" autoFocus />
                          </td>
                          <td className="px-4 py-2">
                            <input type="time" value={editForm.start_time} onChange={e => setEditForm(p => ({...p, start_time: e.target.value}))}
                              className="border dark:border-slate-600 rounded-lg px-2.5 py-1.5 text-sm dark:bg-slate-900 dark:text-white" />
                          </td>
                          <td className="px-4 py-2">
                            <input type="time" value={editForm.end_time} onChange={e => setEditForm(p => ({...p, end_time: e.target.value}))}
                              className="border dark:border-slate-600 rounded-lg px-2.5 py-1.5 text-sm dark:bg-slate-900 dark:text-white" />
                          </td>
                          <td className="px-4 py-2 text-slate-400 text-xs">—</td>
                          <td className="px-4 py-2 text-slate-500 text-xs">{assignedCount} assignment{assignedCount !== 1 ? 's' : ''}</td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleSaveEdit(s.id)} className="p-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600" title="Save"><Check className="w-3.5 h-3.5" /></button>
                              <button onClick={() => setEditingShift(null)} className="p-1.5 bg-slate-100 dark:bg-slate-700 text-slate-500 rounded-lg hover:bg-slate-200" title="Cancel"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3">
                            <span className={`${st.color} border px-2.5 py-1 rounded-lg text-xs font-semibold`}>{s.name}</span>
                          </td>
                          <td className="px-4 py-3 font-mono text-slate-700 dark:text-slate-300 text-sm">
                            {s.start_time?.slice(0, 5)}
                          </td>
                          <td className="px-4 py-3 font-mono text-slate-700 dark:text-slate-300 text-sm">
                            {s.end_time?.slice(0, 5)}
                          </td>
                          <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-sm">
                            {calcDuration(s.start_time?.slice(0, 5) || '00:00', s.end_time?.slice(0, 5) || '00:00')}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${assignedCount > 0 ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>
                              {assignedCount} {assignedCount === 1 ? 'user' : 'users'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleStartEdit(s)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-sky-600 transition-colors" title="Edit shift">
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteShift(s)}
                                disabled={assignedCount > 0}
                                className={`p-1.5 rounded-lg transition-colors ${assignedCount > 0 ? 'text-slate-200 dark:text-slate-700 cursor-not-allowed' : 'hover:bg-rose-50 dark:hover:bg-rose-900/20 text-slate-400 hover:text-rose-500'}`}
                                title={assignedCount > 0 ? `Cannot delete — ${assignedCount} user(s) assigned` : 'Delete shift'}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
                {/* On-Call — system-defined special status, not a database shift */}
                <tr className="bg-rose-50/40 dark:bg-rose-900/10 border-t-2 border-dashed border-rose-200 dark:border-rose-800/40">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="bg-rose-100 text-rose-700 border border-rose-300 px-2.5 py-1 rounded-lg text-xs font-semibold">On-Call</span>
                      <span className="text-xs text-slate-400 italic">system-defined</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-sm italic" colSpan={2}>On demand (any time)</td>
                  <td className="px-4 py-3 text-slate-400 text-sm italic">Variable</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 font-medium">
                      {assignments.filter(a => a.is_on_call).length} active
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-slate-400" title="On-Call is a built-in status and cannot be edited or deleted">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Add New Shift Row */}
          <div className="border-t dark:border-slate-700 pt-4">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">Add New Shift</p>
            <div className="flex gap-2 items-end flex-wrap">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Name</label>
                <input value={newShiftName} onChange={e => setNewShiftName(e.target.value)} placeholder="e.g. Morning"
                  className="border dark:border-slate-700 rounded-lg px-3 py-2 text-sm dark:bg-slate-900 dark:text-white w-36"
                  onKeyDown={e => e.key === 'Enter' && handleCreateShift()} />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Start Time</label>
                <input type="time" value={newShiftStart} onChange={e => setNewShiftStart(e.target.value)}
                  className="border dark:border-slate-700 rounded-lg px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">End Time</label>
                <input type="time" value={newShiftEnd} onChange={e => setNewShiftEnd(e.target.value)}
                  className="border dark:border-slate-700 rounded-lg px-3 py-2 text-sm dark:bg-slate-900 dark:text-white" />
              </div>
              <button onClick={handleCreateShift} disabled={savingShift} className="bg-sky-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-sky-600 flex items-center gap-1.5 disabled:opacity-50">
                {savingShift ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Shift
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legend + Search + Team Filter */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2">
          {shifts.map(s => {
            const st = SHIFT_TYPES.find(t => t.name === s.name) || { color: 'bg-sky-100 text-sky-800 border-sky-300' };
            return <span key={s.id} className={`${st.color} border px-2.5 py-1 rounded-lg text-xs font-medium`}>{s.name}</span>;
          })}
          <span className="bg-rose-100 text-rose-700 border-rose-300 border px-2.5 py-1 rounded-lg text-xs font-medium">On-Call</span>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {/* Application filter → shows only that application's team members */}
          <div className="flex flex-col gap-0.5">
            <select value={appFilter} onChange={e => { setAppFilter(e.target.value); setSearch(''); }}
              className="border dark:border-slate-700 rounded-xl px-3 py-2 text-sm bg-white dark:bg-slate-800 dark:text-white w-52">
              <option value="">All Applications ({users.length} members)</option>
              {applications.map(app => {
                // member_count is available in TeamListSerializer — sum across all teams for this app
                const memberCount = teams
                  .filter(t => String(t.application) === app.id)
                  .reduce((sum, t) => sum + (t.member_count || 0), 0);
                return (
                  <option key={app.id} value={app.id}>
                    {app.name} ({memberCount} members)
                  </option>
                );
              })}
            </select>
            {appFilter && appMemberIds && (
              <p className="text-xs text-sky-600 dark:text-sky-400 pl-1">
                Showing {appMemberIds.size} team member{appMemberIds.size !== 1 ? 's' : ''} for this application
              </p>
            )}
          </div>
          {/* Search within the filtered list */}
          <div className="relative w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder={appFilter ? 'Filter members...' : 'Search users...'}
              className="w-full pl-9 pr-3 py-2 border dark:border-slate-700 rounded-xl text-sm bg-white dark:bg-slate-800 dark:text-white"
              data-testid="search-users" />
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 p-3">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"><ChevronLeft className="w-5 h-5 dark:text-white" /></button>
          <button onClick={() => setCurrentDate(new Date())} className="px-3 py-1.5 text-sm bg-sky-50 dark:bg-sky-900 text-sky-700 dark:text-sky-400 rounded-lg font-medium">Today</button>
          <button onClick={() => navigate(1)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"><ChevronRight className="w-5 h-5 dark:text-white" /></button>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-white ml-2">{title}</h3>
        </div>
        <div className="flex bg-slate-100 dark:bg-slate-700 rounded-lg p-0.5">
          {VIEWS.map(v => (<button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all capitalize ${view === v ? 'bg-white dark:bg-slate-600 text-sky-700 dark:text-sky-400 shadow-sm' : 'text-slate-500 dark:text-slate-400'}`}>{v}</button>))}
        </div>
      </div>

      {/* Assignment Popup */}
      {showAssign && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-lg dark:text-white mb-1">Set Shift</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{users.find(u => u.id === showAssign.userId)?.full_name} — {fmtDate(showAssign.date)}</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {shifts.map(s => {
                const st = SHIFT_TYPES.find(t => t.name === s.name) || { color: 'bg-sky-100 text-sky-800 border-sky-300' };
                return <button key={s.id} onClick={() => handleAssign(showAssign.userId, showAssign.date, s.name)} className={`${st.color} border px-3 py-2.5 rounded-xl text-sm font-medium hover:opacity-80 text-center`}>{s.name}</button>;
              })}
              <button onClick={() => handleAssign(showAssign.userId, showAssign.date, 'On-Call')} className="bg-rose-100 text-rose-700 border-rose-300 border px-3 py-2.5 rounded-xl text-sm font-medium hover:opacity-80">On-Call</button>
              <button onClick={() => handleAssign(showAssign.userId, showAssign.date, 'Off')} className="bg-slate-50 text-slate-400 border-slate-200 border border-dashed px-3 py-2.5 rounded-xl text-sm font-medium hover:opacity-80">Off</button>
            </div>
            <button onClick={() => setShowAssign(null)} className="w-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 py-2 rounded-xl text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* WEEK VIEW (default) */}
      {view === 'week' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 w-52 border-b dark:border-slate-700">
                  Team Member
                </th>
                <th className="px-2 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 w-20 border-b dark:border-slate-700">Status</th>
                {weekDays.map((d, i) => (
                  <th key={i} className={`px-2 py-3 text-center text-xs font-semibold border-b dark:border-slate-700 ${isToday(d) ? 'text-sky-600 bg-sky-50 dark:bg-sky-900/20' : 'text-slate-600 dark:text-slate-400'}`}>
                    <div>{DAYS[d.getDay()]}</div><div className="font-normal text-slate-400">{d.getDate()}/{d.getMonth() + 1}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-700">
              {filteredUsers.map(u => (
                <tr key={u.id} className={`hover:bg-slate-50/50 dark:hover:bg-slate-700/30 ${!u.is_available ? 'opacity-50' : ''}`}>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-sky-100 dark:bg-sky-900 rounded-full flex items-center justify-center text-xs font-semibold text-sky-700 dark:text-sky-400">{(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}</div>
                      <div>
                        <div className="text-sm font-medium dark:text-white">{u.full_name || u.email}</div>
                        <div className="text-xs text-slate-400">{u.role_name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    {isReadOnly ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${u.is_available ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'}`}>
                        {u.is_available ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                        {u.is_available ? 'Active' : 'Away'}
                      </span>
                    ) : (
                      <button onClick={() => toggleAvailability(u)} title={u.is_available ? 'Available — click to mark unavailable' : 'Unavailable — click to mark available'}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${u.is_available ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'}`}>
                        {u.is_available ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                        {u.is_available ? 'Active' : 'Away'}
                      </button>
                    )}
                  </td>
                  {weekDays.map((d, di) => {
                    const a = getAssignment(u.id, d);
                    return (
                      <td key={di} className={`px-2 py-2.5 text-center ${isToday(d) ? 'bg-sky-50/50 dark:bg-sky-900/10' : ''}`}>
                        {isReadOnly ? (
                          <div className="w-full min-h-[36px] rounded-lg flex items-center justify-center">
                            {a ? <ShiftBadge assignment={a} /> : <span className="text-slate-200 dark:text-slate-700 text-xs">—</span>}
                          </div>
                        ) : (
                          <button onClick={() => handleQuickSet(u.id, d)} className="w-full min-h-[36px] rounded-lg border border-dashed border-slate-200 dark:border-slate-600 hover:border-sky-300 dark:hover:border-sky-600 transition-colors flex items-center justify-center">
                            {a ? <ShiftBadge assignment={a} /> : <Plus className="w-3.5 h-3.5 text-slate-300" />}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {filteredUsers.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No users found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* MONTH VIEW */}
      {view === 'month' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="grid grid-cols-7 bg-slate-50 dark:bg-slate-900">
            {DAYS.map(d => <div key={d} className="px-2 py-2.5 text-center text-xs font-semibold text-slate-600 dark:text-slate-400 border-b dark:border-slate-700">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {monthDays.map(({ date, isCurrentMonth }, i) => {
              const dayAssignments = filteredUsers.map(u => ({ user: u, assignment: getAssignment(u.id, date) })).filter(a => a.assignment);
              return (
                <div key={i} onClick={() => { setCurrentDate(date); setView('day'); }} className={`min-h-[90px] p-1.5 border-b border-r dark:border-slate-700 cursor-pointer hover:bg-sky-50/50 dark:hover:bg-sky-900/10 ${!isCurrentMonth ? 'bg-slate-50/50 dark:bg-slate-900/30' : ''} ${isToday(date) ? 'bg-sky-50 dark:bg-sky-900/20' : ''}`}>
                  <div className={`text-xs font-medium mb-1 ${isToday(date) ? 'text-sky-600 font-bold' : isCurrentMonth ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400'}`}>{date.getDate()}</div>
                  <div className="space-y-0.5">
                    {dayAssignments.slice(0, 3).map(({ user: u, assignment: a }) => (
                      <div key={u.id} className="flex items-center gap-1 text-xs"><span className="truncate text-slate-600 dark:text-slate-400 w-12">{u.first_name}</span><ShiftBadge assignment={a} /></div>
                    ))}
                    {dayAssignments.length > 3 && <span className="text-xs text-slate-400">+{dayAssignments.length - 3}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DAY VIEW */}
      {view === 'day' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border dark:border-slate-700 shadow-sm">
          <div className={`px-6 py-4 border-b dark:border-slate-700 ${isToday(currentDate) ? 'bg-sky-50 dark:bg-sky-900/20' : 'bg-slate-50 dark:bg-slate-900'}`}>
            <h3 className="font-semibold text-slate-800 dark:text-white">{currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</h3>
          </div>
          <div className="divide-y dark:divide-slate-700">
            {filteredUsers.map(u => {
              const a = getAssignment(u.id, currentDate);
              return (
                <div key={u.id} className={`px-6 py-4 flex items-center justify-between ${!u.is_available ? 'opacity-50' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-sky-100 dark:bg-sky-900 rounded-full flex items-center justify-center text-sm font-semibold text-sky-700 dark:text-sky-400">{(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}</div>
                    <div>
                      <div className="font-medium dark:text-white">{u.full_name || u.email}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{u.role_name}</div>
                    </div>
                    <button onClick={() => toggleAvailability(u)} className={`ml-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${u.is_available ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                      {u.is_available ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                      {u.is_available ? 'Available' : 'Unavailable'}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    {a ? <ShiftBadge assignment={a} /> : <span className="text-sm text-slate-400">Not scheduled</span>}
                    <button onClick={() => handleQuickSet(u.id, currentDate)} className="ml-2 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 px-3 py-1.5 rounded-lg text-xs hover:bg-sky-100">{a ? 'Change' : 'Set Shift'}</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
